import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import CheckboxSelectSite from './CheckboxSelectSite'
import { useTranslation } from 'react-i18next'
import { CustomDialogTitle } from './CustomerStyledComponents'
import './ModalStyle.scss'
import { deliveryOrderStatuses } from '../../lib/statuses'
import { STATUSES, MODES } from '../../lib/constants'
import MaterialsListVertical from './MaterialsListVertical'
import { SelectStatus } from '../../components'
import DeliveryDateTimePicker from './DeliveryDateTimePicker'
import PrefilledSection from './PrefilledSection'
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  CircularProgress,
} from '@mui/material'
import { useMutation, useQuery } from '@apollo/client'
import { gqlOrderDelivery, gqlConstructionSite, gqlCommand } from '../../gql'
import api from '../../api'
import 'dayjs/locale/fr'
import dayjs from 'dayjs'
dayjs.locale('fr')

function OrderDeliveryModal({ open, onClose, data, mode, commandId, refresh }) {
  const { t } = useTranslation('order_detail')
  const [isSuccessSaved, setIsSuccessSaved] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [isErrorSave, setIsErrorSave] = useState(false)
  const [errorSaveMessage, setErrorSaveMessage] = useState('')
  const sitesConfig = [
    { key: 'startingSite', label: 'Site de départ' },
    { key: 'deliverySite', label: 'Site de livraison' },
    { key: 'unloadingSite', label: 'Site de déchargement' },
  ]
  const [orderDelivery, setOrderDelivery] = useState({
    ...data,
    materials: data.allMaterials?.filter((material) => material.family === 'MATERIAL') || [],
    rubbles: data.allMaterials?.filter((material) => material.family === 'RUBBLE') || [],
    status: data.status || STATUSES.INCOMPLETE,
  })
  const {
    data: constructionSites,
    error: sitesError,
    loading: sitesLoading,
  } = useQuery(gqlConstructionSite.CONSTRUCTION_SITES)
  const {
    data: sitesBdn,
    error: sitesBdnError,
    loading: sitesBdnLoading,
  } = useQuery(gqlConstructionSite.CONSTRUCTION_SITES_BDN)
  const { data: planificationDates } = useQuery(gqlOrderDelivery.DATES_PLANIFICATION)
  const [createOrderDelivery] = useMutation(gqlOrderDelivery.CREATE_ORDER_DELIVERY)
  const [updateOrderDelivery] = useMutation(gqlOrderDelivery.UPDATE_ORDER_DELIVERY)
  const [createOrderDeliveryMaterial] = useMutation(gqlOrderDelivery.CREATE_ORDER_DELIVERY_MATERIAL)
  const [updateOrderDeliveryMaterial] = useMutation(gqlOrderDelivery.UPDATE_ORDER_DELIVERY_MATERIAL)
  const [deleteOrderDeliveryMaterial] = useMutation(gqlOrderDelivery.DELETE_ORDER_DELIVERY_MATERIAL)
  const [materialsToDelete, setMaterialsToDelete] = useState([])
  const [isNotModifiable, setIsNotModifiable] = useState(false)
  const [timeFilterType, setTimeFilterType] = useState(
    data?.deliveryTimeStart && data?.deliveryTimeEnd && data.deliveryTimeStart !== data.deliveryTimeEnd
      ? 'range'
      : 'exact'
  )
  const [clearedPrefilled, setClearedPrefilled] = useState({})
  const [prefilledSites, setPrefilledSites] = useState({})

  const clearPrefilledField = (field) => {
    setClearedPrefilled((prev) => {
      if (!prev[field]) {
        return { ...prev, [field]: true }
      }
      return prev
    })
  }

  const handleClientSitePrefill = (siteKey) => {
    const cmd = command?.command
    if (!cmd) return

    const siteData = {
      isSite: false,
      id: cmd.constructionSiteId || null,
      code: cmd.constructionSite?.code || '',
      address: cmd.constructionSite?.address || cmd.address || '',
      city: cmd.constructionSite?.city || cmd.city || '',
    }

    setOrderDelivery((prevOrder) => ({
      ...prevOrder,
      [siteKey]: siteData,
      [`${siteKey}Bdn`]: null,
      [`${siteKey}BdnId`]: null,
      [`${siteKey}Address`]: siteData.address,
      [`${siteKey}City`]: siteData.city,
      [`${siteKey}Id`]: cmd.constructionSiteId || null,
    }))

    setPrefilledSites((prev) => ({ ...prev, [siteKey]: true }))
  }

  useEffect(() => {
    if (data) {
      const notModifiable = data.status === STATUSES.CANCELED || data.status === STATUSES.VALID
      setIsNotModifiable(notModifiable)
    }
  }, [orderDelivery])

  const {
    data: command,
    errorCommand,
    loadingCommand,
  } = useQuery(gqlCommand.COMMAND_BY_ID, {
    variables: { id: commandId },
  })

  const { data: existingDeliveries } = useQuery(gqlOrderDelivery.ORDER_DELIVERIES_BY_COMMAND_ID, {
    variables: { commandId, first: 1 },
  })
  const showPrefilled = mode === MODES.CREATE && command?.command && existingDeliveries?.orderDeliveries
  const loading = sitesLoading || loadingCommand || sitesBdnLoading
  const error = sitesError || errorCommand || sitesBdnError

  let availableSiteCodes =
    constructionSites?.constructionSites.nodes.map(({ address, city, code, id, postcode }) => ({
      address,
      city,
      code,
      id,
      postcode,
    })) || []

  let availableSitesBdn =
    sitesBdn?.constructionSites.nodes.map(({ address, city, code, id, postcode }) => ({
      address,
      city,
      code,
      id,
      postcode,
    })) || []

  let availableMaterials =
    command?.command.commandMaterials.edges
      .filter(({ node }) => node.material.productCategoryByIdProductCategory.family === 'MATERIAL')
      .map(({ node }) => ({
        id: node.id,
        commandMaterialId: node.commandId,
        materialId: node.material.id,
        description: node.material.description,
        baseMeasureUnitCode: node.material.measureUnitByBaseMeasureUnitCode.abbr,
        internalId: node.material.internalId,
        unit: node.material.measureUnitByBaseMeasureUnitCode.abbr,
        totalQuantity: node.quantity,
      })) || []

  let availableRubbles =
    command?.command.commandMaterials.edges
      .filter(({ node }) => node.material.productCategoryByIdProductCategory.family === 'RUBBLE')
      .map(({ node }) => ({
        id: node.id,
        commandMaterialId: node.commandId,
        materialId: node.material.id,
        description: node.material.description,
        baseMeasureUnitCode: node.material.measureUnitByBaseMeasureUnitCode.abbr,
        internalId: node.material.internalId,
        unit: node.material.measureUnitByBaseMeasureUnitCode.abbr,
        totalQuantity: node.quantity,
      })) || []

  const hasPrefilled = useRef(false)

  useEffect(() => {
    if (mode === MODES.CREATE && command?.command && existingDeliveries?.orderDeliveries && !hasPrefilled.current) {
      const cmd = command.command

      const existingOl = existingDeliveries?.orderDeliveries.edges[0]?.node

      let deliveryDate = ''
      let deliveryTimeStart = ''
      let deliveryTimeEnd = ''
      if (existingOl?.deliveryTimeStart && existingOl?.deliveryTimeEnd && existingOl.deliveryTimeStart !== existingOl.deliveryTimeEnd) {
        deliveryDate = existingOl.deliveryDate || ''
        deliveryTimeStart = existingOl.deliveryTimeStart
        deliveryTimeEnd = existingOl.deliveryTimeEnd
      } else if (cmd.dateDelivery) {
        const d = dayjs(cmd.dateDelivery)
        deliveryDate = d.format('YYYY-MM-DD')
        deliveryTimeStart = d.format('HH:mm')
        deliveryTimeEnd = deliveryTimeStart
      }

      const materials = availableMaterials.map((m) => ({
        id: '',
        commandMaterialId: m.id,
        materialId: m.materialId,
        internalId: m.internalId,
        quantity: m.totalQuantity,
        unit: m.unit,
        totalQuantity: m.totalQuantity,
      }))

      const rubbles = availableRubbles.map((r) => ({
        id: '',
        commandMaterialId: r.id,
        materialId: r.materialId,
        internalId: r.internalId,
        quantity: r.totalQuantity,
        unit: r.unit,
        totalQuantity: r.totalQuantity,
      }))

      setOrderDelivery((prev) => ({
        ...prev,
        deliveryDate,
        deliveryTimeStart: deliveryTimeStart || prev.deliveryTimeStart,
        deliveryTimeEnd: deliveryTimeEnd || prev.deliveryTimeEnd,
        materials,
        rubbles,
        commentPublic: cmd.comment || '',
      }))

      if (deliveryTimeStart && deliveryTimeEnd) {
        setTimeFilterType(deliveryTimeStart !== deliveryTimeEnd ? 'range' : 'exact')
      }

      hasPrefilled.current = true
    }
  }, [command, existingDeliveries, mode])
  // Function to check if the selected date exists in the list
  const isDateInArray = (dates, orderDeliveryDate) => {
    return dates.some((item) => dayjs(item.sentPlanificationDate).isSame(orderDeliveryDate, 'day'))
  }
  /// Fonction mise a jour les sites de depart de livraison et de déchargement si changement
  const handleSiteDetailsChange = (details, siteKey) => {
    setPrefilledSites((prev) => ({ ...prev, [siteKey]: false }))
    const updateOrderDelivery = (
      site = null,
      bdn = null,
      bdnId = null,
      address = null,
      city = null,
      id = null
    ) => {
      setOrderDelivery((prevOrder) => ({
        ...prevOrder,
        [siteKey]: site,
        [`${siteKey}Bdn`]: bdn,
        [`${siteKey}BdnId`]: bdnId,
        [`${siteKey}Address`]: address,
        [`${siteKey}City`]: city,
        [`${siteKey}Id`]: id,
      }))
    }

    if (details.isDepot) {
      const matchedSiteCode = availableSitesBdn?.find((siteCode) => siteCode.id === details.id)
      updateOrderDelivery(
        null,
        {
          isSite: true,
          id: matchedSiteCode?.id || '',
          code: matchedSiteCode?.code || '',
          address: matchedSiteCode?.address || '',
          city: matchedSiteCode?.city || '',
        },
        matchedSiteCode?.id || null
      )
    } else if (details.id) {
      updateOrderDelivery(
        {
          isSite: false,
          id: details.id || null,
          code: details.code || '',
          address: details.address || '',
          city: details.city || '',
        },
        null,
        null,
        null,
        null,
        details.id || null
      )
    } else {
      updateOrderDelivery(null, null, null, details.address || null, details.city || null)
    }
  }

  /// Fonction ajout un materiau ou un déblais
  const handleAddMaterials = (type) => {
    if (type !== 'materials' && type !== 'rubbles') {
      //@toDo gestion des erreur saisie matériaux
      console.error('Invalid type specified:', type)
      return
    }
    clearPrefilledField(type === 'materials' ? 'materials' : 'rubbles')

    setOrderDelivery((prevOrder) => ({
      ...prevOrder,
      [type]: [
        ...prevOrder[type],
        {
          id: '',
          commandMaterialId: '',
          materialId: '',
          internalId: '',
          quantity: 30,
          unit: '',
          totalQuantity: '',
        },
      ],
    }))
  }

  /// Fonction mise a jours du tableau matériaux ou déblais si chnagement
  const handleMaterialsChange = (index, field, value, type) => {
    clearPrefilledField(type === 'materials' ? 'materials' : 'rubbles')
    const newMaterials = [...orderDelivery[type]]
    newMaterials[index][field] = value ?? ''
    if (field === 'name') {
      const availableList = type === 'materials' ? availableMaterials : availableRubbles
      const selectedMaterial = availableList.find((material) => material.internalId === value)
      newMaterials[index] = {
        ...newMaterials[index],
        id: '',
        commandMaterialId: selectedMaterial ? selectedMaterial.id : '',
        materialId: selectedMaterial ? selectedMaterial.materialId : '',
        internalId: selectedMaterial ? selectedMaterial.internalId : '',
        unit: selectedMaterial ? selectedMaterial.unit : '',
        totalQuantity: selectedMaterial ? +selectedMaterial.totalQuantity : '',
      }
    }
    setOrderDelivery((prevOrder) => ({
      ...prevOrder,
      [type]: newMaterials,
    }))
  }

  /// Fonction supprimer un materiau ou un déblais par son index dans la liste
  const handleRemoveMaterials = async (index, type) => {
    clearPrefilledField(type === 'materials' ? 'materials' : 'rubbles')
    const newMaterials = [...orderDelivery[type]]
    if (newMaterials[index].id !== '') {
      setMaterialsToDelete((prev) => [...prev, newMaterials[index].id])
    }
    newMaterials.splice(index, 1)
    setOrderDelivery((prevOrder) => ({
      ...prevOrder,
      [type]: newMaterials,
    }))
  }

  /// Fonction mise a jour status si un nouvel est slectionné
  const handleStatusChange = (event) => {
    setOrderDelivery((prevOrder) => ({
      ...prevOrder,
      status: event.target.value,
    }))
  }

  const handleResetToCommandValues = () => {
    const cmd = command?.command
    if (!cmd) return

    let deliveryDate = ''
    let deliveryTimeStart = ''
    let deliveryTimeEnd = ''
    if (cmd.dateDelivery) {
      const d = dayjs(cmd.dateDelivery)
      deliveryDate = d.format('YYYY-MM-DD')
      deliveryTimeStart = d.format('HH:mm')
      deliveryTimeEnd = deliveryTimeStart
    }
    setTimeFilterType('exact')

    let deliverySite = null
    let deliverySiteId = null
    if (cmd.constructionSite) {
      deliverySite = {
        isSite: false,
        id: cmd.constructionSiteId || '',
        code: cmd.constructionSite.code || '',
        address: cmd.constructionSite.address || '',
        city: cmd.constructionSite.city || '',
      }
      deliverySiteId = cmd.constructionSiteId || null
    }
    const deliverySiteAddress = cmd.constructionSite?.address || cmd.address || ''
    const deliverySiteCity = cmd.constructionSite?.city || cmd.city || ''

    setOrderDelivery((prevOrder) => ({
      ...prevOrder,
      deliveryDate,
      deliveryTimeStart,
      deliveryTimeEnd,
      deliverySite,
      deliverySiteId,
      deliverySiteAddress,
      deliverySiteCity,
      materials: availableMaterials.map((m) => ({
        id: '',
        commandMaterialId: m.id,
        materialId: m.materialId,
        internalId: m.internalId,
        quantity: m.totalQuantity,
        unit: m.unit,
        totalQuantity: m.totalQuantity,
      })),
      rubbles: availableRubbles.map((r) => ({
        id: '',
        commandMaterialId: r.id,
        materialId: r.materialId,
        internalId: r.internalId,
        quantity: r.totalQuantity,
        unit: r.unit,
        totalQuantity: r.totalQuantity,
      })),
      commentPublic: cmd.comment || '',
    }))
    setClearedPrefilled({})
    setPrefilledSites({ deliverySite: true })
  }

  const handleTimeFilterTypeChange = (type) => {
    clearPrefilledField('deliveryDateTime')
    setTimeFilterType(type)
    if (type === 'exact') {
      setOrderDelivery((prevOrder) => ({
        ...prevOrder,
        deliveryTimeEnd: prevOrder.deliveryTimeStart,
      }))
    }
  }

  /**fonction renvoie message de succes ou d'erreur de sauvegarde en fonction
   * du mode creation , edition ou duplication
   * cas erreur ou success
   */
  const getResultMessage = (mode, result) => {
    return t(`${result}.${mode}`)
  }

  //sauvgarde de l'ordre de livraison
  const handleSave = async () => {
    try {
      // Determine and set the status based on the current order details
      const orderForSave = getOrderWithUpdatedStatus()
      //si planning déja envoyé a la date de l'ol ,
      //=> alors mettre a jour l'ol pour permettre les notifications lors des modifications
      const dates = planificationDates?.planifications?.nodes || []
      if (
        dates.length &&
        isDateInArray(dates, orderForSave.deliveryDate) &&
        !orderForSave.isPlanificationSent
      ) {
        // Mettre à jour le statut de la planification
        orderForSave.isPlanificationSent = true
      }
      // Create, update, or duplicate the order based on the mode
      if (mode === MODES.DUPLICATE) {
        await handleCreateOrder(orderForSave)
      } else {
        // Handle deletion of materials
        await handleMaterialDeletion()
        if (mode === MODES.CREATE) {
          await handleCreateOrder(orderForSave)
        } else if (mode === MODES.EDIT) {
          await handleEditOrder(orderForSave)
        }
      }
      // Handle success
      handleSuccess()
    } catch (error) {
      // Handle error
      handleError()
    }
  }

  // Function to handle deletion of materials
  const handleMaterialDeletion = async () => {
    if (materialsToDelete.length > 0) {
      for (const materialId of materialsToDelete) {
        await deleteOrderDeliveryMaterial({
          variables: { id: materialId },
        })
      }
      setMaterialsToDelete([]) // Clear the materialsToDelete after deletion
    }
  }

  // Function to get the updated status for the order
  const getOrderWithUpdatedStatus = () => {
    let orderForSave = { ...orderDelivery }

    // Handle userId ( id of user associated to delivery order) based on duplication mode
    const isUser = mode === MODES.DUPLICATE ? false : Boolean(orderDelivery.userId)
    if (!isUser) orderForSave.userId = null

    // Define conditions for statuses
    const isComplete =
      command?.command.status === STATUSES.COMPLETE &&
      orderDelivery.deliveryDate &&
      (orderDelivery.startingSiteBdnId || orderDelivery.startingSiteId || orderDelivery.startingSiteAddress) &&
      (orderDelivery.deliverySiteBdnId || orderDelivery.deliverySiteId || orderDelivery.deliverySiteAddress) &&
      (orderDelivery.materials.length > 0 || orderDelivery.rubbles.length > 0);
  
    const isPlanified = isComplete && isUser;
  
    const isValid =
      isComplete &&
      mode !== MODES.DUPLICATE &&
      orderDelivery.scanCmr &&
      orderDelivery.scanProviderDeliveryNote &&
      orderDelivery.scanSignature

    // Determine the status based on conditions
    if (isValid && orderForSave.status !== STATUSES.CANCELED) {
      orderForSave.status = STATUSES.VALID
    } else if (isPlanified && ![STATUSES.VALID, STATUSES.CANCELED].includes(orderForSave.status)) {
      orderForSave.status = STATUSES.PLANIFIED
    } else if (
      isComplete &&
      ![STATUSES.PLANIFIED, STATUSES.VALID, STATUSES.CANCELED].includes(orderForSave.status)
    ) {
      orderForSave.status = STATUSES.COMPLETE
    }

    return orderForSave
  }

  // Function to handle order creation
  const handleCreateOrder = async (orderForSave) => {
    const { data } = await createOrderDelivery({
      variables: { ...orderForSave },
    })
    const orderDeliveryId = data.createOrderDelivery.orderDelivery.id

    // Handle materials and rubbles creation
    await handleMaterialCreation(orderForSave.materials, orderDeliveryId)
    await handleMaterialCreation(orderForSave.rubbles, orderDeliveryId)
  }

  // Function to handle order editing
  const handleEditOrder = async (orderForSave) => {
    // Effectuer la mise à jour de l'ordre de livraison
    await updateOrderDelivery({
      variables: { ...orderForSave },
    })
    // Filter and handle material updates and creation
    await handleMaterialUpdate(orderForSave.materials)
    await handleMaterialUpdate(orderForSave.rubbles)

    const materialsToCreate = orderForSave.materials.filter((material) => material.id === '')
    const rubblesToCreate = orderForSave.rubbles.filter((material) => material.id === '')

    await handleMaterialCreation(materialsToCreate, orderForSave.id)
    await handleMaterialCreation(rubblesToCreate, orderForSave.id)

    //Envoie des notifications
    //Verification si planning déja envoyé + si status OL est planifié
    if (orderForSave.isPlanificationSent && orderForSave.status == STATUSES.PLANIFIED) {
      // envoie de notification au chauffeur concerné
      const driverId = orderForSave.userId
      api.sendNotification(driverId).catch(() => {
        console.error('error sending notification to driver with Id', driverId)
      })
    }
  }

  // Function to handle material creation
  const handleMaterialCreation = async (materials, orderDeliveryId) => {
    for (const material of materials) {
      await createOrderDeliveryMaterial({
        variables: {
          commandMaterialId: material.commandMaterialId,
          orderDeliveryId,
          materialId: material.materialId,
          quantity: +material.quantity,
        },
      })
    }
  }

  // Function to handle material update
  const handleMaterialUpdate = async (materials) => {
    const materialsToUpdate = materials.filter((material) => material.id !== '')
    for (const material of materialsToUpdate) {
      await updateOrderDeliveryMaterial({
        variables: {
          id: material.id,
          materialId: material.materialId,
          quantity: +material.quantity,
        },
      })
    }
  }

  // Handle success message display
  const handleSuccess = () => {
    setSuccessMessage(getResultMessage(mode, 'success'))
    setIsSuccessSaved(true)
    setTimeout(() => {
      setIsSuccessSaved(false)
      onClose()
      refresh()
    }, 1000)
  }

  // Handle error message display
  const handleError = () => {
    setErrorSaveMessage(getResultMessage(mode, 'error'))
    setIsErrorSave(true)
    setTimeout(() => {
      setIsErrorSave(false)
    }, 4000)
  }

  const getTitleModal = () => {
    return t(`titleModal.${mode}`)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ sx: { width: '100%', m: 0 } }}>
      <CustomDialogTitle>
        <Box>
          <h5>{getTitleModal()}</h5>
        </Box>
        <Button
          style={{
            color: 'inherit',
            fontSize: '1rem',
          }}
          onClick={onClose}
        >
          X
        </Button>
      </CustomDialogTitle>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress
            size={80}
            sx={{
              svg: {
                width: '100%',
                height: '100%',
                viewBox: '0 0 40 40',
              },
            }}
          />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography color="error">{t('errorLoadingData')}</Typography>
        </Box>
      ) : (
        <DialogContent sx={{ minHeight: '60vh' }}>
          {/*affichage des messages d'erreur et de success*/}
          {isSuccessSaved && <Box className="success-message">{successMessage}</Box>}
          {isErrorSave && <Box className="error-message">{errorSaveMessage}</Box>}
          <Box
            sx={{
              borderRadius: '4px',
              background: '#F3F3F3',
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              my: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                background: '#fff',
                borderRadius: '4px',
                padding: '0 0 0 12px',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t('statusLabel')}
              </Typography>
              <SelectStatus
                status={orderDelivery.status}
                statuses={deliveryOrderStatuses()}
                isDisabled={isNotModifiable}
                onChange={(e) => handleStatusChange(e)}
              />
            </Box>
            <Button variant="outlined" sx={{ background: '#fff' }} onClick={handleResetToCommandValues}>
              {t('resetToCommandValues')}
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={5}>
              <PrefilledSection show={(showPrefilled || hasPrefilled.current) && !clearedPrefilled.deliveryDateTime}>
                <DeliveryDateTimePicker
                  deliveryDate={orderDelivery.deliveryDate}
                  deliveryTimeStart={orderDelivery.deliveryTimeStart}
                  deliveryTimeEnd={orderDelivery.deliveryTimeEnd}
                  timeFilterType={timeFilterType}
                  onDeliveryDateChange={(value) => {
                    clearPrefilledField('deliveryDateTime')
                    setOrderDelivery((prevOrder) => ({ ...prevOrder, deliveryDate: value }))
                  }}
                  onDeliveryTimeStartChange={(value) => {
                    clearPrefilledField('deliveryDateTime')
                    setOrderDelivery((prevOrder) => ({
                      ...prevOrder,
                      deliveryTimeStart: value,
                      deliveryTimeEnd: timeFilterType === 'exact' ? value : prevOrder.deliveryTimeEnd || value,
                    }))
                  }}
                  onDeliveryTimeEndChange={(value) => {
                    clearPrefilledField('deliveryDateTime')
                    setOrderDelivery((prevOrder) => ({ ...prevOrder, deliveryTimeEnd: value }))
                  }}
                  onTimeFilterTypeChange={handleTimeFilterTypeChange}
                  isNotModifiable={isNotModifiable}
                />
              </PrefilledSection>
              {sitesConfig.map((config) => (
                <PrefilledSection
                  key={config.key}
                  show={prefilledSites[config.key] === true}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{config.label}</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleClientSitePrefill(config.key)}
                      disabled={isNotModifiable}
                      sx={{ mt: '5px' }}
                    >
                      {t('client')}
                    </Button>
                  </Box>
                  <CheckboxSelectSite
                    availableSites={availableSiteCodes}
                    availableSitesBdn={availableSitesBdn}
                    label={config.label}
                    siteKey={config.key}
                    onChange={handleSiteDetailsChange}
                    orderDelivery={orderDelivery}
                    isNotModifiable={isNotModifiable}
                  />
                </PrefilledSection>
              ))}
            </Grid>

            <Grid item xs={4}>
              <PrefilledSection show={(showPrefilled || hasPrefilled.current) && !clearedPrefilled.materials}>
                <MaterialsListVertical
                  materials={orderDelivery.materials}
                  availableMaterials={availableMaterials?.map((material) => ({
                    ...material,
                    id: material.id,
                    commandMaterialId: material.commandMaterialId,
                    materialId: material.materialId,
                    internalId: material.internalId,
                    unit: material.unit,
                  }))}
                  handleAddMaterial={() => handleAddMaterials('materials')}
                  handleMaterialChange={handleMaterialsChange}
                  handleRemoveMaterial={handleRemoveMaterials}
                  title={t('materials')}
                  buttonTitle={t('addMaterial')}
                  isNotModifiable={isNotModifiable}
                  type="materials"
                />
              </PrefilledSection>
              <PrefilledSection show={(showPrefilled || hasPrefilled.current) && !clearedPrefilled.rubbles} mt={2}>
                <MaterialsListVertical
                  materials={orderDelivery.rubbles}
                  availableMaterials={availableRubbles?.map((material) => ({
                    ...material,
                    id: material.id,
                    commandMaterialId: material.commandMaterialId,
                    materialId: material.materialId,
                    internalId: material.internalId,
                    unit: material.unit,
                  }))}
                  handleAddMaterial={() => handleAddMaterials('rubbles')}
                  handleMaterialChange={handleMaterialsChange}
                  handleRemoveMaterial={handleRemoveMaterials}
                  title={t('rubbles')}
                  buttonTitle={t('addRubble')}
                  isNotModifiable={isNotModifiable}
                  type="rubbles"
                />
              </PrefilledSection>
            </Grid>

            <Grid item xs={3}>
              <Typography variant="h6" gutterBottom>{t('comments')}</Typography>
              <PrefilledSection show={(showPrefilled || hasPrefilled.current) && !clearedPrefilled.commentPublic}>
                <TextField
                  label={t('publicComment')}
                  fullWidth
                  multiline
                  rows={4}
                  value={orderDelivery.commentPublic}
                  onChange={(e) => {
                    clearPrefilledField('commentPublic')
                    setOrderDelivery((prevOrder) => ({
                      ...prevOrder,
                      commentPublic: e.target.value,
                    }))
                  }}
                  disabled={isNotModifiable}
                  margin="normal"
                />
              </PrefilledSection>
              <TextField
                label={t('privateComment')}
                fullWidth
                multiline
                rows={4}
                value={orderDelivery.commentPrivate}
                onChange={(e) =>
                  setOrderDelivery((prevOrder) => ({
                    ...prevOrder,
                    commentPrivate: e.target.value,
                  }))
                }
                disabled={isNotModifiable}
                margin="normal"
              />
              <TextField
                label={t('driverComment')}
                fullWidth
                multiline
                rows={4}
                value={orderDelivery.commentDriver}
                onChange={(e) =>
                  setOrderDelivery((prevOrder) => ({
                    ...prevOrder,
                    commentDriver: e.target.value,
                  }))
                }
                disabled
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="warning">
          {t('cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={isNotModifiable}>
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

OrderDeliveryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  mode: PropTypes.oneOf([MODES.CREATE, MODES.EDIT, MODES.DUPLICATE]).isRequired,
  commandId: PropTypes.string.isRequired,
  refresh: PropTypes.func.isRequired,
}

export default OrderDeliveryModal
